/**
 * Platform admin hub — users, force-unpublish, stats, link to DIGIPIN admin.
 * Route: /admin (admin role only)
 */
import { useEffect, useState } from 'react';
import { Link as RouterLink, Navigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Paper,
  TextField,
  Typography,
  CircularProgress,
  Chip,
} from '@mui/material';
import { PublicSiteChrome, publicPageSx } from '../site/PublicSiteChrome';
import Footer from '../Footer';
import { useAppSelector } from '../../store/hooks';
import {
  adminGetPlatformStats,
  adminListUsers,
  adminDisableUser,
  adminEnableUser,
  adminUnpublishShare,
  adminGetFunnelSummary,
  type AdminUserRow,
} from '../../services/adminPlatformService';
import { adminListLeads, adminUpdateLead, type TierLeadRow } from '../../services/leadsService';
import { adminListPayments } from '../../services/paymentsService';

type AdminPaymentRow = {
  id?: string;
  email?: string;
  name?: string;
  organization?: string;
  plan_code?: string;
  amount_paise?: number;
  currency?: string;
  status?: string;
  razorpay_order_id?: string;
  razorpay_payment_id?: string;
  merchant_label?: string;
  product_label?: string;
  paid_at?: string;
  created_at?: string;
};

const TOKENS = {
  accent: '#D97706',
  secondary: '#3B82F6',
  green: '#10B981',
  danger: '#EF4444',
  bgCard: 'rgba(15, 23, 42, 0.75)',
  border: 'rgba(219, 234, 254, 0.15)',
  textPrimary: '#F8FAFC',
  textSecondary: '#94A3B8',
};

export default function AdminHubPage() {
  const user = useAppSelector((s) => s.auth.user);
  const isAuthenticated = useAppSelector((s) => s.auth.isAuthenticated);
  const [stats, setStats] = useState<{
    users: number;
    projects: number;
    layers: number;
    disabledUsers: number;
  } | null>(null);
  const [users, setUsers] = useState<AdminUserRow[]>([]);
  const [leads, setLeads] = useState<TierLeadRow[]>([]);
  const [payments, setPayments] = useState<AdminPaymentRow[]>([]);
  const [funnel, setFunnel] = useState<{
    uniqueSessions: number;
    byEvent: Record<string, number>;
    funnel: {
      map_open: number;
      pricing_view: number;
      lead_submit: number;
      map_to_pricing_pct: number | null;
      pricing_to_lead_pct: number | null;
    };
  } | null>(null);
  const [q, setQ] = useState('');
  const [layerId, setLayerId] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = async (search?: string) => {
    setLoading(true);
    setError(null);
    try {
      const [s, u, l, f, p] = await Promise.all([
        adminGetPlatformStats(),
        adminListUsers(search ?? q),
        adminListLeads().catch(() => ({ success: false, leads: [] as TierLeadRow[] })),
        adminGetFunnelSummary(7).catch(() => null),
        adminListPayments().catch(() => ({
          success: false,
          payments: [] as AdminPaymentRow[],
          count: 0,
        })),
      ]);
      if (s.success) setStats(s.stats);
      if (u.success) setUsers(u.users);
      if (l.success) setLeads(l.leads || []);
      if (f && f.success) {
        setFunnel({
          uniqueSessions: f.uniqueSessions,
          byEvent: f.byEvent,
          funnel: f.funnel,
        });
      }
      if (p.success) setPayments((p.payments || []) as AdminPaymentRow[]);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated && user?.role === 'admin') {
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user?.role]);

  if (!isAuthenticated) {
    return <Navigate to="/login?redirect=%2Fadmin" replace />;
  }
  if (user && user.role !== 'admin') {
    return <Navigate to="/account" replace />;
  }

  const onDisable = async (id: string, email: string) => {
    if (!window.confirm(`Disable ${email}? Sessions and DIGIPIN keys will be revoked.`)) return;
    setBusy(true);
    setMessage(null);
    try {
      await adminDisableUser(id, 'Disabled from admin hub');
      setMessage(`Disabled ${email}`);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Disable failed');
    } finally {
      setBusy(false);
    }
  };

  const onEnable = async (id: string, email: string) => {
    setBusy(true);
    setMessage(null);
    try {
      await adminEnableUser(id);
      setMessage(`Re-enabled ${email}`);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Enable failed');
    } finally {
      setBusy(false);
    }
  };

  const onUnpublish = async () => {
    if (!layerId.trim()) return;
    if (!window.confirm(`Force-unpublish /m/${layerId.trim()}?`)) return;
    setBusy(true);
    setMessage(null);
    setError(null);
    try {
      const res = (await adminUnpublishShare(layerId.trim())) as { message?: string };
      setMessage(res.message || 'Unpublished');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unpublish failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ ...publicPageSx, bgcolor: '#0B132B', color: TOKENS.textPrimary }}>
      <PublicSiteChrome title="GeoVera" subtitle="Admin" active="admin" />
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        <Typography sx={{ fontSize: 28, fontWeight: 800, mb: 0.5 }}>Admin hub</Typography>
        <Typography sx={{ color: TOKENS.textSecondary, fontSize: 13, mb: 3 }}>
          Platform ops · Studio payments (SIFA Geolabs) · DIGIPIN keys · force-unpublish
        </Typography>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 3 }}>
          <Button
            component={RouterLink}
            to="/admin/api-keys"
            variant="contained"
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              bgcolor: TOKENS.accent,
              '&:hover': { bgcolor: '#B45309' },
            }}
          >
            DIGIPIN API keys
          </Button>
          <Button
            component={RouterLink}
            to="/account"
            variant="outlined"
            sx={{ textTransform: 'none', borderColor: TOKENS.border, color: TOKENS.textPrimary }}
          >
            My account
          </Button>
        </Box>

        {message && (
          <Paper
            sx={{
              p: 1.5,
              mb: 2,
              bgcolor: 'rgba(16,185,129,0.12)',
              border: '1px solid rgba(16,185,129,0.35)',
            }}
          >
            <Typography sx={{ color: '#6EE7B7', fontSize: 13 }}>{message}</Typography>
          </Paper>
        )}
        {error && (
          <Paper
            sx={{
              p: 1.5,
              mb: 2,
              bgcolor: 'rgba(239,68,68,0.1)',
              border: '1px solid rgba(239,68,68,0.35)',
            }}
          >
            <Typography sx={{ color: '#FCA5A5', fontSize: 13 }}>{error}</Typography>
          </Paper>
        )}

        {loading && !stats ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress sx={{ color: TOKENS.secondary }} />
          </Box>
        ) : (
          <>
            {stats && (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
                  gap: 2,
                  mb: 3,
                }}
              >
                {[
                  ['Users', stats.users],
                  ['Projects', stats.projects],
                  ['Layers', stats.layers],
                  ['Disabled', stats.disabledUsers],
                ].map(([label, val]) => (
                  <Paper
                    key={String(label)}
                    elevation={0}
                    sx={{
                      p: 2,
                      bgcolor: TOKENS.bgCard,
                      border: `1px solid ${TOKENS.border}`,
                      borderRadius: 2,
                    }}
                  >
                    <Typography sx={{ fontSize: 11, color: TOKENS.textSecondary, fontWeight: 700 }}>
                      {label}
                    </Typography>
                    <Typography sx={{ fontSize: 28, fontWeight: 800 }}>{val}</Typography>
                  </Paper>
                ))}
              </Box>
            )}

            {/* Growth funnel (7 days) */}
            {funnel && (
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  mb: 3,
                  bgcolor: TOKENS.bgCard,
                  border: `1px solid ${TOKENS.border}`,
                  borderRadius: 3,
                }}
              >
                <Typography sx={{ fontWeight: 800, mb: 1.5 }}>Growth funnel (7 days)</Typography>
                <Typography sx={{ fontSize: 12, color: TOKENS.textSecondary, mb: 2 }}>
                  Unique sessions: {funnel.uniqueSessions}
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(5, 1fr)' },
                    gap: 1.5,
                    mb: 2,
                  }}
                >
                  {[
                    ['Map opens', funnel.funnel.map_open],
                    ['Pricing views', funnel.funnel.pricing_view],
                    ['Leads', funnel.funnel.lead_submit],
                    ['Map→Pricing %', funnel.funnel.map_to_pricing_pct ?? '—'],
                    ['Pricing→Lead %', funnel.funnel.pricing_to_lead_pct ?? '—'],
                  ].map(([label, val]) => (
                    <Box key={String(label)}>
                      <Typography sx={{ fontSize: 11, color: TOKENS.textSecondary }}>
                        {label}
                      </Typography>
                      <Typography sx={{ fontSize: 22, fontWeight: 800 }}>{val}</Typography>
                    </Box>
                  ))}
                </Box>
                <Typography sx={{ fontSize: 11, color: '#64748B' }}>
                  Events:{' '}
                  {Object.entries(funnel.byEvent)
                    .map(([k, v]) => `${k}=${v}`)
                    .join(' · ') || 'none yet'}
                </Typography>
              </Paper>
            )}

            {/* Studio payments (Razorpay / SIFA Geolabs) */}
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                mb: 3,
                bgcolor: TOKENS.bgCard,
                border: `1px solid ${TOKENS.border}`,
                borderRadius: 3,
              }}
            >
              <Typography sx={{ fontWeight: 800, mb: 0.5 }}>
                Studio payments ({payments.length})
              </Typography>
              <Typography sx={{ fontSize: 12, color: TOKENS.textSecondary, mb: 1.5 }}>
                Razorpay · merchant SIFA Geolabs · product GeoVera Studio
              </Typography>
              {(() => {
                const paid = payments.filter((x) => x.status === 'paid');
                const revenueInr =
                  paid.reduce((sum, x) => sum + (Number(x.amount_paise) || 0), 0) / 100;
                const pending = payments.filter(
                  (x) => x.status === 'created' || x.status === 'pending'
                ).length;
                return (
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(3, 1fr)' },
                      gap: 1.5,
                      mb: 2,
                    }}
                  >
                    <Box>
                      <Typography sx={{ fontSize: 11, color: TOKENS.textSecondary }}>
                        Paid
                      </Typography>
                      <Typography sx={{ fontSize: 22, fontWeight: 800 }}>{paid.length}</Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: 11, color: TOKENS.textSecondary }}>
                        Revenue (paid)
                      </Typography>
                      <Typography sx={{ fontSize: 22, fontWeight: 800 }}>
                        ₹{revenueInr.toLocaleString('en-IN')}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: 11, color: TOKENS.textSecondary }}>
                        Open orders
                      </Typography>
                      <Typography sx={{ fontSize: 22, fontWeight: 800 }}>{pending}</Typography>
                    </Box>
                  </Box>
                );
              })()}
              {payments.length === 0 ? (
                <Typography sx={{ color: TOKENS.textSecondary, fontSize: 13 }}>
                  No Studio payments yet. Checkout is on /pricing.
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {payments.slice(0, 50).map((pay, idx) => (
                    <Box
                      key={String(pay.id || pay.razorpay_order_id || idx)}
                      sx={{
                        py: 1.25,
                        borderBottom: `1px solid ${TOKENS.border}`,
                        display: 'flex',
                        flexWrap: 'wrap',
                        justifyContent: 'space-between',
                        gap: 1,
                        alignItems: 'flex-start',
                      }}
                    >
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Box
                          sx={{
                            display: 'flex',
                            gap: 1,
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            mb: 0.5,
                          }}
                        >
                          <Chip
                            size="small"
                            label={pay.plan_code || 'studio'}
                            sx={{
                              fontWeight: 700,
                              fontSize: 10,
                              bgcolor: 'rgba(217,119,6,0.2)',
                              color: '#FBBF24',
                            }}
                          />
                          <Chip
                            size="small"
                            label={pay.status || 'unknown'}
                            sx={{
                              fontWeight: 600,
                              fontSize: 10,
                              bgcolor:
                                pay.status === 'paid'
                                  ? 'rgba(16,185,129,0.15)'
                                  : 'rgba(255,255,255,0.06)',
                              color: pay.status === 'paid' ? '#6EE7B7' : '#CBD5E1',
                            }}
                          />
                        </Box>
                        <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                          {pay.name || '—'} · {pay.email || '—'}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: TOKENS.textSecondary }}>
                          {pay.organization || '—'} · ₹
                          {((Number(pay.amount_paise) || 0) / 100).toLocaleString('en-IN')}
                          {pay.paid_at
                            ? ` · paid ${new Date(pay.paid_at).toLocaleString('en-IN')}`
                            : pay.created_at
                              ? ` · created ${new Date(pay.created_at).toLocaleString('en-IN')}`
                              : ''}
                        </Typography>
                        {pay.razorpay_payment_id && (
                          <Typography
                            sx={{
                              fontSize: 11,
                              color: '#64748B',
                              mt: 0.25,
                              fontFamily: 'monospace',
                            }}
                          >
                            {pay.razorpay_payment_id}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Paper>

            {/* Studio / Enterprise leads */}
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                mb: 3,
                bgcolor: TOKENS.bgCard,
                border: `1px solid ${TOKENS.border}`,
                borderRadius: 3,
              }}
            >
              <Typography sx={{ fontWeight: 800, mb: 1.5 }}>
                Studio / Enterprise leads ({leads.length})
              </Typography>
              {leads.length === 0 ? (
                <Typography sx={{ color: TOKENS.textSecondary, fontSize: 13 }}>
                  No leads yet. Pricing form posts to /api/leads.
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                  {leads.slice(0, 40).map((lead) => (
                    <Box
                      key={lead.id}
                      sx={{
                        py: 1.25,
                        borderBottom: `1px solid ${TOKENS.border}`,
                        display: 'flex',
                        flexWrap: 'wrap',
                        justifyContent: 'space-between',
                        gap: 1,
                        alignItems: 'flex-start',
                      }}
                    >
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Box
                          sx={{
                            display: 'flex',
                            gap: 1,
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            mb: 0.5,
                          }}
                        >
                          <Chip
                            size="small"
                            label={lead.tier}
                            sx={{
                              textTransform: 'uppercase',
                              fontWeight: 700,
                              fontSize: 10,
                              bgcolor:
                                lead.tier === 'enterprise'
                                  ? 'rgba(217,119,6,0.2)'
                                  : 'rgba(59,130,246,0.2)',
                              color: lead.tier === 'enterprise' ? '#FBBF24' : '#93C5FD',
                            }}
                          />
                          <Chip
                            size="small"
                            label={lead.status}
                            sx={{
                              fontWeight: 600,
                              fontSize: 10,
                              bgcolor: 'rgba(255,255,255,0.06)',
                              color: '#CBD5E1',
                            }}
                          />
                        </Box>
                        <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                          {lead.name} · {lead.email}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: TOKENS.textSecondary }}>
                          {lead.organization || '—'} ·{' '}
                          {lead.createdAt ? new Date(lead.createdAt).toLocaleString() : ''}
                        </Typography>
                        {lead.message && (
                          <Typography sx={{ fontSize: 12, color: '#94A3B8', mt: 0.5 }}>
                            {lead.message}
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {['contacted', 'qualified', 'closed', 'spam'].map((st) => (
                          <Button
                            key={st}
                            size="small"
                            disabled={busy || lead.status === st}
                            onClick={async () => {
                              setBusy(true);
                              setMessage(null);
                              try {
                                await adminUpdateLead(lead.id, { status: st });
                                setMessage(`Lead marked ${st}`);
                                await load();
                              } catch (e) {
                                setError(e instanceof Error ? e.message : 'Update failed');
                              } finally {
                                setBusy(false);
                              }
                            }}
                            sx={{
                              textTransform: 'none',
                              fontSize: 11,
                              color: TOKENS.textSecondary,
                              minWidth: 0,
                              px: 1,
                            }}
                          >
                            {st}
                          </Button>
                        ))}
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Paper>

            {/* Force unpublish */}
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                mb: 3,
                bgcolor: TOKENS.bgCard,
                border: `1px solid ${TOKENS.border}`,
                borderRadius: 3,
              }}
            >
              <Typography sx={{ fontWeight: 800, mb: 1.5 }}>Force-unpublish public map</Typography>
              <Typography sx={{ fontSize: 12, color: TOKENS.textSecondary, mb: 1.5 }}>
                Sets share_enabled=false so /m/&lt;layerId&gt; returns 404. Requires migration 08.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <TextField
                  size="small"
                  placeholder="Layer UUID"
                  value={layerId}
                  onChange={(e) => setLayerId(e.target.value)}
                  sx={{
                    minWidth: 280,
                    flex: 1,
                    '& .MuiOutlinedInput-root': { color: '#F8FAFC' },
                    '& fieldset': { borderColor: TOKENS.border },
                  }}
                />
                <Button
                  variant="contained"
                  disabled={busy || !layerId.trim()}
                  onClick={onUnpublish}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    bgcolor: TOKENS.danger,
                    '&:hover': { bgcolor: '#DC2626' },
                  }}
                >
                  Unpublish
                </Button>
              </Box>
            </Paper>

            {/* Users */}
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                bgcolor: TOKENS.bgCard,
                border: `1px solid ${TOKENS.border}`,
                borderRadius: 3,
              }}
            >
              <Typography sx={{ fontWeight: 800, mb: 1.5 }}>Users</Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                <TextField
                  size="small"
                  placeholder="Search email or id"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && load(q)}
                  sx={{
                    minWidth: 240,
                    flex: 1,
                    '& .MuiOutlinedInput-root': { color: '#F8FAFC' },
                    '& fieldset': { borderColor: TOKENS.border },
                  }}
                />
                <Button
                  variant="outlined"
                  onClick={() => load(q)}
                  disabled={busy}
                  sx={{
                    textTransform: 'none',
                    borderColor: TOKENS.border,
                    color: TOKENS.textPrimary,
                  }}
                >
                  Search
                </Button>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {users.map((u) => (
                  <Box
                    key={u.id}
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: 1,
                      py: 1.25,
                      borderBottom: `1px solid ${TOKENS.border}`,
                    }}
                  >
                    <Box>
                      <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{u.email}</Typography>
                      <Typography sx={{ fontSize: 11, color: TOKENS.textSecondary }}>
                        {u.id.slice(0, 8)}… · {u.role}
                        {u.lastLogin ? ` · last ${new Date(u.lastLogin).toLocaleDateString()}` : ''}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      {u.isDisabled ? (
                        <Chip
                          size="small"
                          label="Disabled"
                          sx={{ bgcolor: 'rgba(239,68,68,0.2)', color: '#FCA5A5' }}
                        />
                      ) : (
                        <Chip
                          size="small"
                          label="Active"
                          sx={{ bgcolor: 'rgba(16,185,129,0.15)', color: '#6EE7B7' }}
                        />
                      )}
                      {u.isDisabled ? (
                        <Button
                          size="small"
                          disabled={busy}
                          onClick={() => onEnable(u.id, u.email)}
                          sx={{ textTransform: 'none', color: TOKENS.green }}
                        >
                          Enable
                        </Button>
                      ) : (
                        <Button
                          size="small"
                          disabled={busy || u.role === 'admin'}
                          onClick={() => onDisable(u.id, u.email)}
                          sx={{ textTransform: 'none', color: TOKENS.danger }}
                        >
                          Disable
                        </Button>
                      )}
                    </Box>
                  </Box>
                ))}
                {users.length === 0 && (
                  <Typography sx={{ color: TOKENS.textSecondary, fontSize: 13 }}>
                    No users found
                  </Typography>
                )}
              </Box>
            </Paper>
          </>
        )}
      </Container>
      <Footer compact />
    </Box>
  );
}
